// ThemeContext — Restful Night-Mode with Light + Auto options.
// Applies the `data-app-theme` attribute on <html> ONLY when the user is
// inside authenticated pages (via ThemeScope wrapper in AppLayout).
// Public pages (Welcome/Login/Register/Privacy/Terms) remain warm/light.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'sd_theme_mode';
const DEFAULT_MODE = 'dark'; // Per user pivot: default to Dark for authenticated users

const ThemeContext = createContext(null);

const getSystemPreference = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const readStoredMode = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'auto') return v;
  } catch (_) { /* ignore */ }
  return DEFAULT_MODE;
};

const resolveEffective = (mode) => {
  if (mode === 'auto') return getSystemPreference();
  return mode;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => readStoredMode());
  const [systemPref, setSystemPref] = useState(() => getSystemPreference());
  // Whether theme should be applied on <html> — controlled by ThemeScope
  const [scopeActive, setScopeActive] = useState(false);

  // Track system preference changes for 'auto' mode
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPref(e.matches ? 'dark' : 'light');
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  const effective = mode === 'auto' ? systemPref : mode;

  // Apply/remove the theme attribute on the <html> element based on scope + effective
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (scopeActive) {
      root.setAttribute('data-app-theme', effective === 'dark' ? 'night' : 'day');
    } else {
      root.removeAttribute('data-app-theme');
    }
    return () => {
      // safety: clear on unmount
      if (!scopeActive) root.removeAttribute('data-app-theme');
    };
  }, [scopeActive, effective]);

  const updateMode = useCallback((next) => {
    setMode(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
  }, []);

  const value = useMemo(() => ({
    mode,
    effective,
    setMode: updateMode,
    scopeActive,
    setScopeActive,
  }), [mode, effective, updateMode, scopeActive]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

// ThemeScope — mount this inside authenticated layouts to activate the theme.
// It automatically deactivates on unmount so public pages stay light.
export const ThemeScope = ({ children }) => {
  const { setScopeActive } = useTheme();
  useEffect(() => {
    setScopeActive(true);
    return () => setScopeActive(false);
  }, [setScopeActive]);
  return <>{children}</>;
};

export default ThemeContext;
